<template>
    <div>
        <Accordion :activeIndex="activeTab" @tab-open="tabOpen">
            <AccordionTab header="General Information" :key="editDataSource.name">
                <div class="p-fluid formgrid grid">
                    <div class="field col-12 md:col-4">
                        <label for="name">Name</label>
                        <InputText maxlength = "255" id="name" v-model="editDataSource.name" :class="{ 'p-invalid': submitted && !editDataSource.name }" placeholder="Enter data source name" />
                        <small class="p-invalid" v-if="submitted && !editDataSource.name">Data Source Name is required.</small>
                    </div>
                    <div class="field col-12 md:col-4">
                        <label for="code">Code</label>
                        <InputText maxlength = "255" id="code" v-model="editDataSource.code" :class="{ 'p-invalid': submitted && !editDataSource.code }" placeholder="Enter data source code" />
                        <small class="p-invalid" v-if="submitted && !editDataSource.code">Data Source Code is required.</small>
                    </div>
                    <div class="field col-12 md:col-4">
                        <label for="shareable">Shareable</label><br />
                        <ToggleButton id="shareable" v-model="editDataSource.sharable" onLabel="True" offLabel="False" :style="{ width: '7em' }" />
                    </div>
                    <div class="field col-12">
                        <label for="description">Description</label>
                        <Textarea rows="5" v-model="editDataSource.description" />
                    </div>
                </div>
                <Divider />
                <div class="form-actions">
                    <Button @click="switchTab('next')" label="Next" />
                </div>
            </AccordionTab>

            <AccordionTab header="Data Source Links">
                <div>
                    <!-- Parent level -->
                    <div class="font-bold">
                        <span>Start by selecting the desired logical operator</span>
                    </div>
                    <Divider />
                    <!-- Topmost Parent Logical Operation selection for links -->
                    <data-source-pro-links v-if="showParentInnerLinks" :currentLinks="editDataSource.links" :parent="true" />
                    <Divider />
                    <div v-if="userGroupsDetail.length > 0">
                        <p>
                            User Groups based on this DataSource:
                        </p>
                        <DataTable :value="userGroupsDetail">
                            <Column field="userGroup.id" header="Id"></Column>
                            <Column field="userGroup.name" header="Name"></Column>
                            <Column field="userGroup.code" header="Code"></Column>
                            <Column field="userGroup.description" header="Description"></Column>

                            <Column field="users" header="Users" ref="users" key="userGroup.id">
                                <template #body="slotProps">
                                    <Button icon="pi pi-user" label="Users" :badge="slotProps.data.users.length.toString()" class="m-2 p-button p-button-warning" v-tooltip="'Show User Group members'" @click="toggleUserGroupMemberWindows(slotProps.data.users, slotProps.data.userGroup.name)"> </Button>
                                </template>
                            </Column>
                            <Column field="userGroup.id" header="Edit">
                                <template #body="slotProps">
                                    <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" v-tooltip="'Edit User Group'" @click="editUserGroup(slotProps.data.userGroup.id)" :disabled="!checkUserPermission(UserPermissions.WRITE_USER_GROUP)" />
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                    <div v-else-if="checkUserPermission(UserPermissions.WRITE_USER_GROUP)">
                        No User Group is currently linked with this Datasource. Do you want to create one? <br />
                        <b>Please note:</b> the Datasource will be saved at the end of the process and the previous page
                        will be displayed.<br />
                        <Button label="Add New User Group" icon="pi pi-plus" class="mb-2 mt-2" @click="createUserGroup(null)" :disabled="userGroupCreateInProgress" />
                    </div>
                    <div v-else>
                        No User Group is currently linked with this Datasource.
                    </div>
                    <Divider />
                    <div class="">
                        <Button label="Submit Changes" @click="submit" />
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </div>

    <Dialog v-model:visible="userGroupWindowsShow" modal>
        <template #header>
            <div class="inline-flex align-items-center justify-content-center gap-2">
                <span class=" white-space-nowrap">Members of UserGroup: <b>{{userGroupWindowsGroupName}}</b></span>
            </div>
        </template>

        <DataTable :value="userGroupWindowsMembers" :paginator="true" :rows="5" responsiveLayout="scroll">
            <template #empty> No Users available </template>
            <Column field="id" header="ID" />
            <Column field="name" header="Name" />
            <Column field="surname" header="Surname" />
        </DataTable>
    </Dialog>

</template>

<script setup>
import DataSourceProLinks from '../modules/DataSourceProLinks.vue';

// computed,
import { ref, computed, onMounted, defineProps } from 'vue';
import { storeToRefs } from 'pinia';
import { useDataSourceStore } from '../store';
import GlobalRequest from '@/api/global-request';
import useUserComposable from '@/composables/UserComposable';
import { UserPermissions } from '@/enums';
import router from '@/router';

const { checkUserPermission } = useUserComposable();

// props
const props = defineProps({
    id: {
        type: [Number, String],
    },
});

const dataSourceStore = useDataSourceStore();
const { dataSource, editDataSource } = storeToRefs(dataSourceStore);

// states
const activeTab = ref(1);
const submitted = ref(false);

// computed properties

// for the topmost parent operator in the links
// check if display value is true
const showParentInnerLinks = computed(() => {
    return editDataSource.value?.links[0]?.logical_operation;
});

onMounted(async () => {
    // extract datasource details
    await loadDataSourceDetails();
});

const userGroupWindowsShow = ref(false)
const userGroupWindowsGroupName = ref('')
const userGroupWindowsMembers = ref([])

function editUserGroup(userGroupId) {
    router.push({ name: 'UpdateDatasourceUserGroups', params: { id: userGroupId } })
}

const userGroupCreateInProgress = ref(false);

function createUserGroup() {
    userGroupCreateInProgress.value = true;

    GlobalRequest.postRequest('v2/datasource/users', {
        company_id: company_id.value,
        name: editDataSource.value.name,
        code: editDataSource.value.code,
        description: editDataSource.value.description,
        datasource_id: props.id,
    }).then((response) => {
        if (response.statusCode === 201) {
            submit().then(() => {
                router.go(-1)
            });
        }
    });
}

function toggleUserGroupMemberWindows(users, userGroupName) {
    userGroupWindowsShow.value = true;
    userGroupWindowsGroupName.value = userGroupName;
    userGroupWindowsMembers.value = users;
}

const { company_id } = useUserComposable();

const userGroupsDetail = ref([]);

//actions
// load the data source details and format it properly
const loadDataSourceDetails = async () => {
    const dataSourceDetails = await dataSourceStore.fetchDataSourceDetails(props.id);

    if (Object.hasOwn(dataSourceDetails, 'used_by_user_groups')) {
        dataSourceDetails.used_by_user_groups.forEach((userGroupId) => {

            GlobalRequest.getRequest('v2/datasource/users/'+userGroupId, {
                params: {
                    company_id: company_id.value,
                },
            }).then((response) => {
                if (response.statusCode === 200) {
                    userGroupsDetail.value.push(response.data)
                }
            });
        })
    }

    dataSourceStore.setupDataSource(dataSourceDetails);
};

const submit = async () => {
    // console.log('Submitting datasource');
    // check for validation first
    const dataInvalid = await dataSourceStore.isLinkInvalid();

    if (!dataInvalid) {
        dataSource.value = { ...editDataSource.value };
        dataSource.value.links = dataSourceStore.cleanLinks(dataSource.value.links);

        //console.log('editDataSourceId.value', editDataSourceId.value)

        await dataSourceStore.updateDataSource();
    }
};

// helpers
const tabOpen = (event) => {
    activeTab.value = event.index;
};

const switchTab = async (direction) => {
    if (direction === 'next') {
        activeTab.value++;
    } else {
        activeTab.value--;
    }
};
</script>
